import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { TicketCreateRequestModel } from '~app/models/requests/ticket-create-request.model';
import { TicketCreateModel } from '~app/models/requests/ticket-create.model';
import { TicketEditModel } from '~app/models/requests/ticket-edit.model';
import { ApiResponseModel } from '~app/models/responses/api-response.model';
import { SupportPlanModel } from '~app/models/responses/support-plan.model';
import { TicketCreateResponseModel } from '~app/models/responses/ticket-create-response.model';
import { TicketListModel } from '~app/models/responses/ticket-list.model';
import { TicketViewerModel } from '~app/models/responses/ticket-viewer.model';
import { UserTicketTimeSummaryModel } from '~app/models/responses/user-ticket-time-summary.model';
import { TicketMessageOfferTypeModel } from '~app/models/ticket-message-offer-type.model';
import { TicketMessageModel } from '~app/models/ticket-message.model';
import { TicketModel } from '~app/models/ticket.model';
import { BaseCrudStore } from '~core/abstractions/base-crud.store';
import { StoreConfig } from '~core/models/store.config';

@Injectable({
    providedIn: 'root'
})
export class TicketsStore extends BaseCrudStore<TicketModel, TicketListModel, TicketEditModel> {
    constructor() {
        super(new StoreConfig({ baseController: 'tickets' }));
    }

    getSupportPlan(licenseId: number): Observable<ApiResponseModel<SupportPlanModel>> {
        return this._requestService.makeGet<ApiResponseModel<SupportPlanModel>>(this.getUrl(`support-plan/${licenseId}`));
    }

    createTicket(requestId: string, requestModel: TicketCreateModel): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl(`${requestId}/create`), requestModel);
    }

    resolveTicket(requestId: string): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePost<ApiResponseModel<boolean>>(this.getUrl(`${requestId}/resolve`));
    }

    createTicketRequest(requestModel: TicketCreateRequestModel): Observable<ApiResponseModel<TicketCreateResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<TicketCreateResponseModel>>(this.getUrl('request'), requestModel);
    }

    updateTicketTime(ticketId: number): Observable<ApiResponseModel<TicketViewerModel[]>> {
        return this._requestService.makePut<ApiResponseModel<TicketViewerModel[]>>(this.getUrl(`${ticketId}/ticket-time`));
    }

    acceptOffer(offerId: number): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePut<ApiResponseModel<boolean>>(this.getUrl(`offer/${offerId}/accept`));
    }

    getAssistantAnswer(ticketId: number): Observable<ApiResponseModel<TicketMessageModel>> {
        return this._requestService.makePut<ApiResponseModel<TicketMessageModel>>(this.getUrl(`${ticketId}/assistant-answer`));
    }

    getEnhancedAnswer(ticketId: number, text: string): Observable<ApiResponseModel<string>> {
        return this._requestService.makePut<ApiResponseModel<string>>(this.getUrl(`${ticketId}/enhanced-answer`), { text: text });
    }

    declineOffers(ticketMessageId: number): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makePut<ApiResponseModel<boolean>>(this.getUrl(`message/${ticketMessageId}/decline-offers`));
    }

    deleteMessage(ticketMessageId: number): Observable<ApiResponseModel<boolean>> {
        return this._requestService.makeDelete<ApiResponseModel<boolean>>(this.getUrl(`message/${ticketMessageId}`));
    }

    getDefaultsForOfferType(offerTypeId: number): Observable<ApiResponseModel<TicketMessageOfferTypeModel>> {
        return this._requestService.makeGet<ApiResponseModel<TicketMessageOfferTypeModel>>(
            this.getUrl(`message-offer-type/${offerTypeId}`)
        );
    }

    getTicketTimeSummary(ticketId: number): Observable<ApiResponseModel<UserTicketTimeSummaryModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<UserTicketTimeSummaryModel[]>>(this.getUrl(`${ticketId}/ticket-time-summary`));
    }
}
